import React, { useEffect, useState, useRef, forwardRef, useMemo } from 'react';
import './About.css';
import founderImage from '../images/founder.jpeg';

const About = forwardRef((props, ref) => {
    const [decryptedFields, setDecryptedFields] = useState({});
    const [startAnimation, setStartAnimation] = useState(false);
    const hasAnimated = useRef(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    // Field values to decrypt
    const fieldData = useMemo(() => ({
        name: 'John E. Greif IV',
        title: 'President',
        experience: '20+ Years',
        homeBase: 'Palm Beach',
        operations: 'Global'
    }), []);

    const triggerAnimation = () => {
        if (hasAnimated.current) return; // Prevent re-triggering
        hasAnimated.current = true;
        setStartAnimation(true);
    };

    const handleCardClick = () => {
        hasAnimated.current = false; // Allow re-triggering
        setStartAnimation(false); // Reset animation
        setTimeout(() => {
            triggerAnimation();
        }, 50); // Delay to ensure state change is detected
    };

    // Decryption animation logic
    useEffect(() => {
        if (!startAnimation) {
            const emptyFields = Object.keys(fieldData).reduce((acc, key) => {
                acc[key] = '\u00A0'; // Non-breaking space
                return acc;
            }, {});
            setDecryptedFields(emptyFields);
            return;
        }

        const decryptField = (fieldId, finalValue, delay) => {
            setTimeout(() => {
                const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
                let iterations = 0;
                const maxIterations = finalValue.length * 3;

                const interval = setInterval(() => {
                    const currentValue = finalValue
                        .split('')
                        .map((char, index) => {
                            if (index < iterations / 3) {
                                return char;
                            }
                            // Preserve spaces and special characters
                            if (char === ' ' || char === ',' || char === '.' || char === '+' || char === '-') {
                                return char;
                            }
                            return chars[Math.floor(Math.random() * chars.length)];
                        })
                        .join('');

                    setDecryptedFields(prev => ({
                        ...prev,
                        [fieldId]: currentValue
                    }));

                    if (iterations >= maxIterations) {
                        clearInterval(interval);
                        setDecryptedFields(prev => ({
                            ...prev,
                            [fieldId]: finalValue
                        }));
                    }

                    iterations++;
                }, 30);
            }, delay);
        };

        decryptField('name', fieldData.name, 500);
        decryptField('title', fieldData.title, 900);
        decryptField('experience', fieldData.experience, 1300);
        decryptField('homeBase', fieldData.homeBase, 1700);
        decryptField('operations', fieldData.operations, 2100);
    }, [startAnimation, fieldData]);

    // Intersection Observer to trigger animation
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    triggerAnimation();
                }
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: 0.5 // Trigger when 50% of the component is visible
            }
        );

        const currentRef = ref.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [ref]);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const bioStyle = {
        marginBottom: windowWidth < 450 ? '-50px' : (windowWidth < 1000 ? '50px' : '0px')
    };

    return (
        <section id="about" ref={ref}>
            <div className="id-card-container">
                <div className="id-card-wrapper">
                    <div className="id-card" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
                        <div className="id-card-left">
                            <div className="id-card-photo">
                                <img src={founderImage} alt="Founder John E. Greif IV" />
                            </div>
                            <div className="id-card-signature">J. Greif IV</div>
                        </div>
                        <div className="id-card-right">
                            <div className="id-card-header">
                                <div className="id-card-header-logo">Aerogen, Inc.</div>
                                <div className="id-card-header-title">IDENTIFICATION CARD</div>
                            </div>
                            <div className="id-card-info">
                                <div className="info-row">
                                    <div className="info-field">
                                        <span className="field-label">NAME</span>
                                        <span className="field-value decrypting" data-value={fieldData.name}>
                                            {decryptedFields.name || '\u00A0'}
                                        </span>
                                    </div>
                                </div>
                                <div className="info-row">
                                    <div className="info-field">
                                        <span className="field-label">TITLE</span>
                                        <span className="field-value decrypting" data-value={fieldData.title}>
                                            {decryptedFields.title || '\u00A0'}
                                        </span>
                                    </div>
                                    <div className="info-field">
                                        <span className="field-label">EXPERIENCE</span>
                                        <span className="field-value decrypting" data-value={fieldData.experience}>
                                            {decryptedFields.experience || '\u00A0'}
                                        </span>
                                    </div>
                                </div>
                                <div className="info-row">
                                    <div className="info-field">
                                        <span className="field-label">HOME BASE</span>
                                        <span className="field-value decrypting" data-value={fieldData.homeBase}>
                                            {decryptedFields.homeBase || '\u00A0'}
                                        </span>
                                    </div>
                                    <div className="info-field">
                                        <span className="field-label">AOR</span>
                                        <span className="field-value decrypting" data-value={fieldData.operations}>
                                            {decryptedFields.operations || '\u00A0'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* <div className="id-card-watermark">
                            <img src="/images/logo.png" alt="Aerogen Watermark" />
                        </div> */}
                    </div>
                </div>
                <div style={bioStyle} className="founder-bio-wrapper">
                    <div className="founder-bio">
                        <div className="tactical-label">Leadership Profile</div>
                        <p>
                            Aviation's been my life from the start. My father founded Tropic Air in Belize in 1979,
                            now the country's biggest airline, and I grew up in that world-—absorbing it from the
                            gate to the cockpit.
                        </p>
                        <p>
                            That pull took me to the U.S. Air Force, where I worked on C-17s, then into 15+ years leading operations at Tropic Air and Southern Airways Express, the largest commuter airlines in their respective countries. Along the way, I helped expand routes, cut costs, and built teams that got the job
                            done. Now, with Aerogen, Inc., I'm channeling that into helping businesses grow smarter and
                            stronger.
                        </p>
                        <p>
                            At home, I find my anchor with my wife and five kids.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
});

export default About; 